import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, MessageCircle, Loader2 } from "lucide-react";
import { BRAND, SERVICES } from "@/data/content";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StarDash } from "@/components/StarDash";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  name: "",
  email: "",
  phone: "",
  service: "",
  message: "",
};

const ContactSection = () => {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (key) => (e) =>
    setForm((s) => ({ ...s, [key]: e.target.value }));

  const buildWaMessage = (f) => {
    const lines = [
      "Hi INC. Cleaning,",
      "",
      "I'd like a quote — here are my details:",
      "",
      `Name:    ${f.name}`,
      `Email:   ${f.email}`,
      `Phone:   ${f.phone}`,
      `Service: ${f.service}`,
      "",
      "Details:",
      f.message,
    ];
    return lines.join("\n");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.name.trim() ||
      !form.email.trim() ||
      !form.phone.trim() ||
      !form.service ||
      !form.message.trim()
    ) {
      toast.error("Please fill in all fields before sending.");
      return;
    }

    // Open WhatsApp synchronously inside the click handler so popup blockers
    // don't intercept the new tab (most browsers require user-gesture).
    const waUrl = `${BRAND.whatsapp}?text=${encodeURIComponent(buildWaMessage(form))}`;
    const waWindow = window.open(waUrl, "_blank", "noopener,noreferrer");

    setSubmitting(true);
    try {
      // Fire-and-save to backend (so nothing is lost). Don't block on result.
      await axios.post(`${API}/inquiries`, form).catch(() => {});
      toast.success("Opening WhatsApp — just hit send to deliver your message.");
      setForm(initialState);
    } finally {
      setSubmitting(false);
      // Fallback: if popup was blocked, redirect current tab.
      if (!waWindow || waWindow.closed) {
        window.location.href = waUrl;
      }
    }
  };

  const waLink = `${BRAND.whatsapp}?text=${BRAND.whatsappText}`;

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 sm:py-32 bg-[#f5ede1]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
        <div className="text-center reveal">
          <StarDash />
          <div className="mt-6 font-sans-body text-[11px] font-bold uppercase tracking-[0.32em] text-[#6b5a4a]">
            ★ Get a Free Quote
          </div>
          <h2 className="font-display mt-4 text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-[#2b1a14] leading-[0.95]">
            Tell us about
            <br />
            <span className="font-italic-serif text-[#c63027]">your space.</span>
          </h2>
          <p className="font-italic-serif mt-5 text-base sm:text-lg text-[#5a4636]">
            Send the form below or WhatsApp us directly. We reply same working day.
          </p>
        </div>

        <div className="mt-12 sm:mt-14 grid lg:grid-cols-12 gap-6 sm:gap-8">
          {/* Form */}
          <div className="lg:col-span-7 reveal">
            <form
              onSubmit={handleSubmit}
              className="sharp relative bg-[#fbf3e6] p-5 sm:p-10"
              data-testid="contact-form"
            >
              <div className="absolute inset-3 pointer-events-none border border-[#c63027]/30" />
              <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.28em] text-[#6b5a4a] mb-2 font-sans-body">
                    ★ Your name
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={handleChange("name")}
                    placeholder="Your full name"
                    className="w-full bg-transparent border-b-2 border-[#c63027]/30 focus:border-[#c63027] px-1 py-2 text-base text-[#2b1a14] placeholder:text-[#a89478] outline-none transition-colors"
                    data-testid="contact-input-name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.28em] text-[#6b5a4a] mb-2 font-sans-body">
                    ★ Email
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={handleChange("email")}
                    placeholder="you@email.com"
                    className="w-full bg-transparent border-b-2 border-[#c63027]/30 focus:border-[#c63027] px-1 py-2 text-base text-[#2b1a14] placeholder:text-[#a89478] outline-none transition-colors"
                    data-testid="contact-input-email"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.28em] text-[#6b5a4a] mb-2 font-sans-body">
                    ★ Phone
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={handleChange("phone")}
                    placeholder="+60 19-731 8121"
                    className="w-full bg-transparent border-b-2 border-[#c63027]/30 focus:border-[#c63027] px-1 py-2 text-base text-[#2b1a14] placeholder:text-[#a89478] outline-none transition-colors"
                    data-testid="contact-input-phone"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.28em] text-[#6b5a4a] mb-2 font-sans-body">
                    ★ Service needed
                  </label>
                  <Select
                    value={form.service}
                    onValueChange={(v) => setForm((s) => ({ ...s, service: v }))}
                  >
                    <SelectTrigger
                      className="w-full bg-transparent rounded-none border-0 border-b-2 border-[#c63027]/30 focus:border-[#c63027] focus:ring-0 h-11 px-1 text-base data-[placeholder]:text-[#a89478]"
                      data-testid="contact-select-service"
                    >
                      <SelectValue placeholder="Pick a service" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-[#fbf3e6] border-[#c63027]/30">
                      {SERVICES.map((s) => (
                        <SelectItem
                          key={s.title}
                          value={s.title}
                          className="rounded-none focus:bg-[#c63027]/10"
                        >
                          {s.title}
                        </SelectItem>
                      ))}
                      <SelectItem
                        value="Not sure — please advise"
                        className="rounded-none focus:bg-[#c63027]/10"
                      >
                        Not sure — please advise
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[10px] font-bold uppercase tracking-[0.28em] text-[#6b5a4a] mb-2 font-sans-body">
                    ★ Tell us about your space
                  </label>
                  <textarea
                    rows={5}
                    value={form.message}
                    onChange={handleChange("message")}
                    placeholder="Property type, size, location, any specific concerns…"
                    className="w-full bg-transparent border-b-2 border-[#c63027]/30 focus:border-[#c63027] px-1 py-2 text-base text-[#2b1a14] placeholder:text-[#a89478] outline-none resize-none transition-colors"
                    data-testid="contact-input-message"
                    required
                  />
                </div>
                <div className="sm:col-span-2 flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between mt-2">
                  <p className="font-italic-serif text-[#6b5a4a]">
                    Your details open in WhatsApp pre-filled — just hit send.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-btn"
                    className="pill-btn inline-flex items-center justify-center gap-2 h-13 px-7 py-3.5 bg-[#25D366] hover:bg-[#1ebe5b] disabled:bg-[#a9c9b3] text-white transition-colors"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="size-4 animate-spin" />
                        Opening WhatsApp…
                      </>
                    ) : (
                      <>
                        <MessageCircle className="size-4" />
                        Send via WhatsApp
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Details */}
          <div className="lg:col-span-5 reveal flex flex-col gap-6">
            <div className="relative bg-[#c63027] text-[#fbf3e6] p-8 sm:p-10 overflow-hidden">
              <div className="absolute inset-3 pointer-events-none border border-[#fbf3e6]/30" />
              <div className="relative">
                <div className="font-sans-body text-[10px] font-bold uppercase tracking-[0.32em] text-[#fbf3e6]/85">
                  ★ Direct Line
                </div>
                <h3 className="font-display mt-3 text-3xl font-bold">
                  Speak to <span className="font-italic-serif">us.</span>
                </h3>
                <p className="font-italic-serif mt-3 text-lg text-[#fbf3e6]/90 leading-relaxed">
                  Owner-operated. You'll talk directly to the team
                  responsible for the cleaners at your door.
                </p>

                <div className="mt-7 space-y-5">
                  <a
                    href="tel:+60197318121"
                    data-testid="contact-phone-1"
                    className="flex items-center gap-4 group"
                  >
                    <span className="size-10 grid place-items-center bg-[#fbf3e6]/15 rounded-full group-hover:bg-[#fbf3e6] group-hover:text-[#c63027] transition-colors">
                      <Phone className="size-4" />
                    </span>
                    <div>
                      <div className="font-sans-body text-[10px] uppercase tracking-[0.28em] opacity-80">
                        Phone
                      </div>
                      <div className="font-display font-bold text-lg">019-731 8121</div>
                    </div>
                  </a>
                  <a
                    href={`mailto:${BRAND.email}`}
                    data-testid="contact-email-link"
                    className="flex items-center gap-4 group"
                  >
                    <span className="size-10 grid place-items-center bg-[#fbf3e6]/15 rounded-full group-hover:bg-[#fbf3e6] group-hover:text-[#c63027] transition-colors">
                      <Mail className="size-4" />
                    </span>
                    <div>
                      <div className="font-sans-body text-[10px] uppercase tracking-[0.28em] opacity-80">
                        Email
                      </div>
                      <div className="font-display font-bold text-base break-all">
                        {BRAND.email}
                      </div>
                    </div>
                  </a>
                  <div className="flex items-start gap-4">
                    <span className="size-10 grid place-items-center bg-[#fbf3e6]/15 rounded-full mt-0.5">
                      <MapPin className="size-4" />
                    </span>
                    <div>
                      <div className="font-sans-body text-[10px] uppercase tracking-[0.28em] opacity-80">
                        Office
                      </div>
                      <div className="font-italic-serif text-base leading-snug">
                        {BRAND.address}
                      </div>
                    </div>
                  </div>
                </div>

                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-btn"
                  className="pill-btn mt-8 inline-flex w-full items-center justify-center gap-2 h-13 px-6 py-3.5 bg-[#fbf3e6] hover:bg-white text-[#c63027] transition-colors"
                >
                  <MessageCircle className="size-5" />
                  Chat on WhatsApp
                </a>
              </div>
            </div>

            <div className="relative bg-[#fbf3e6] p-6 border border-[#d9c9b2]">
              <div className="font-sans-body text-[10px] font-bold uppercase tracking-[0.28em] text-[#6b5a4a]">
                ★ Working Hours
              </div>
              <div className="font-sans-body mt-3 text-base text-[#3b2a1f] leading-relaxed">
                Mon — Sun · 7:00am – 7:00pm
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
